import { useState, useEffect, useCallback, useMemo, type MouseEvent } from "react";
import { ChevronDown, Timer, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "./ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { useLanguage } from "../i18n/language-provider";
import { fetchAutoSyncSchedule, updateAutoSyncSchedule } from "../services/autoSyncApi";
import { type NamedAutoSyncSchedule } from "../services/autoSyncApi";
import { fetchConnectionSyncFrequency } from "../services/pmsConnectionsApi";
import {
    DEFAULT_SYNC_FREQUENCY_MINUTES,
    type SyncFrequencyMinutes,
} from "../types/config";
import { syncCadenceLabel, syncCadencePhrase } from "./pms/syncCadence";
import { FULL_HORIZON_DAYS, NEAR_HORIZON_DAYS } from "./pms/syncHorizon";
import { toast } from "sonner";
import { notifySyncConfigChanged } from "../services/syncStatusEvents";
import { PmsSendFilters, type RoomKindOption } from "./PmsSendFilters";
import { useConnectionProvider } from "../hooks/useActiveProviderCapabilities";
import { useMinStayScope } from "../hooks/useMinStayScope";
import { MinStayPerNightNotice } from "./pms/MinStayPerNightNotice";
import { MinStayAloneNotice, sendsMinStayBesideResentPrice } from "./pms/MinStayAloneNotice";
import { pmsDisplayName } from "./drafts/pmsDisplayName";
import { revealKeyboardFocus } from "./pricing/reveal-keyboard-focus";
import { useContentBelowFold } from "../hooks/useContentBelowFold";

export type { RoomKindOption } from "./PmsSendFilters";

interface AutoSyncConfigProps {
    connectionId: number;
    /** Optional list of room kinds to populate the room kind selector.
     *  When not supplied the component shows only the days-ahead selector. */
    roomKindOptions?: RoomKindOption[];
    /**
     * Hand the open state to the caller. The sidebar's sync indicator opens
     * this dialog from its own popover, and a second copy of the form there
     * would be two implementations of one screen. When controlled, the
     * trigger button is not rendered — a button inside a popover would be a
     * way in that nobody asked for, and the caller has its own.
     */
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

/**
 * The form as the server holds it. The fields on screen are local until Save,
 * so this is what they are compared with: to say what is in force while a
 * switch is not saved yet, and to ask before the dialog closes on a change
 * nobody saved.
 */
interface SavedForm {
    enabled: boolean;
    daysAhead: string;
    roomKindIds: readonly string[];
    ratePlanId: string;
    sendPrices: boolean;
    sendMinLos: boolean;
}

/** What a property nobody has saved a schedule for reads as, and what the form opens on. */
const NOTHING_SAVED: SavedForm = {
    enabled: false,
    daysAhead: "7",
    roomKindIds: [],
    ratePlanId: "",
    sendPrices: true,
    sendMinLos: false,
};

/**
 * What closing now would leave unsaved: the switch itself, which decides
 * whether anything is sent at all, or something else about what a sync sends.
 */
type UnsavedChange = "turnedOn" | "turnedOff" | "changed";

/**
 * Cancel and Save, side by side at every width: stacked, the two cost a phone
 * 48px of a footer that never scrolls away. In a narrow dialog they share the
 * row and are 40px to the finger; from 26rem of the dialog's own width they
 * take their own, as in every other dialog. The dialog is as wide as the
 * window on a phone and 28rem beside it, on a root font that differs by
 * window and skin, which a viewport breakpoint in here could not read. 26rem
 * lies between the widest phone (24.9rem) and the dialog's desktop width
 * (27.9rem).
 */
const FOOTER_BUTTON = "h-10 flex-1 @min-[26rem]/auto-sync:h-9 @min-[26rem]/auto-sync:flex-none";

/**
 * How far a press of "Continues below" scrolls the form on: four fifths of
 * what is in view. The last fifth stays, at the top now, to read on from.
 */
const PAGE_SHARE = 0.8;

/**
 * "Continues below", standing on the footer's rule: the rule's own hairline
 * drawn round the words, on the dialog's own surface, so that the rule opens
 * around them and neither it nor the form under them shows through.
 *
 * Small, and in the dialog's full-strength text all the same. It is there to
 * be read at a glance, and a skin's muted text need not be: on this surface
 * it measured 2.9:1 in the Better Hotel skin, which is the skin of OPT-46.
 *
 * 1.5rem tall and lifted by half of that, it lies half over the body's lower
 * edge and half in the footer's padding, and takes no room from either: on a
 * phone held sideways the form has 150 to 241px, and none to give.
 *
 * A finger gets 40px to press: 0.75rem above the words and 0.25rem below
 * them, which is where the footer's 1rem of padding ends and Cancel and Save
 * begin. The strip is placed from inside the hairline, hence the pixel in
 * each of its offsets: without it, it measured 38px. Under a coarse
 * pointer only, and at any width: a phone held sideways is wider than `sm`,
 * and under a mouse the same strip would take the clicks meant for the last
 * line of the form in view.
 */
const CONTINUES_BELOW =
    "pointer-events-auto relative -mt-3 inline-flex h-6 shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-border bg-background pl-1.5 pr-2.5 text-xs font-medium text-foreground outline-none transition-colors duration-150 motion-reduce:transition-none hover:bg-accent hover:text-accent-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 pointer-coarse:before:absolute pointer-coarse:before:-inset-x-px pointer-coarse:before:-top-[calc(0.75rem+1px)] pointer-coarse:before:-bottom-[calc(0.25rem+1px)]";

/**
 * "Continues below" scrolls, as a wheel does, and a wheel moves no focus.
 * Were a press to give it the focus, the focus would go with it once the
 * scroll reaches the form's end: the dialog itself takes it then, and the
 * next Tab starts over at the form's first control.
 */
const keepFocus = (event: MouseEvent<HTMLButtonElement>) => event.preventDefault();

export function AutoSyncConfig({
    connectionId,
    roomKindOptions = [],
    open: controlledOpen,
    onOpenChange,
}: AutoSyncConfigProps) {
    const { t } = useLanguage();
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : uncontrolledOpen;
    const setOpen = useCallback(
        (next: boolean) => {
            if (!isControlled) setUncontrolledOpen(next);
            onOpenChange?.(next);
        },
        [isControlled, onOpenChange],
    );
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Schedule form state
    const [enabled, setEnabled] = useState(false);
    const [daysAhead, setDaysAhead] = useState("7");
    /**
     * Read-only. The cadence decides when the prices this dialog configures
     * actually leave, so the dialog says it — but how often Optivo polls
     * somebody else's PMS is an operational decision support makes per
     * account, so it is set from the admin console alone.
     */
    const [frequencyMinutes, setFrequencyMinutes] = useState<SyncFrequencyMinutes>(
        DEFAULT_SYNC_FREQUENCY_MINUTES,
    );
    const [selectedRoomKinds, setSelectedRoomKinds] = useState<Set<string>>(new Set());
    const [selectedPlanId, setSelectedPlanId] = useState("");
    // Scheduled runs choose their upload scope the same way a manual send does.
    const [sendPrices, setSendPrices] = useState(true);
    const [sendMinLos, setSendMinLos] = useState(false);
    // The PMS behind this dialog's own connection: the Connections page mounts
    // one dialog per property, and only one of them is the active property.
    const provider = useConnectionProvider(connectionId);
    const capabilities = provider?.capabilities ?? null;
    const minLosSupported = capabilities?.supportsRestrictionsPush !== false;
    // Where this property's PMS keeps one minimum stay per night for every
    // room type, a schedule sending minimum stays sends each night the highest
    // of its room types — said before the schedule is saved that way. It
    // informs, like the notices beside it: Save waits on none of them.
    const minStayScope = useMinStayScope(open && enabled && sendMinLos && minLosSupported, connectionId);
    // Where this property's PMS keeps the minimum stay on the price, a
    // schedule that sends minimum stays with prices switched off re-sends each
    // night's price beside them, and leaves a night whose price Optivo cannot
    // vouch for to a later run — said before the schedule is saved that way,
    // since the Upload Log then lists such a night as held back on every run.
    const sendsMinStayAlone = sendsMinStayBesideResentPrice(capabilities, {
        prices: sendPrices,
        minLos: sendMinLos && minLosSupported,
    });

    // Status badge state (independent of dialog)
    const [statusEnabled, setStatusEnabled] = useState<boolean | null>(null);

    // What the server holds, beside the fields above.
    const [saved, setSaved] = useState<SavedForm>(NOTHING_SAVED);
    // Whether the server said Optivo holds no price plan for this dialog's own
    // property. Only its explicit no counts: a server that predates the answer
    // or could not read the plan says nothing, and nothing is then claimed.
    const [pricePlanMissing, setPricePlanMissing] = useState(false);
    // The question asked before a change nobody saved is dropped.
    const [askingToClose, setAskingToClose] = useState(false);
    // The part that scrolls, kept in state and not in a ref: the dialog
    // mounts it only while it is open, and what watches it has to start then.
    const [body, setBody] = useState<HTMLDivElement | null>(null);
    // Save is in view whatever the body hides, so an operator can save
    // without having seen the settings under the fold. Where the fold cuts a
    // box or a line of text the cut says there is more; where it falls
    // between two blocks nothing does, macOS drawing no scrollbar until
    // something scrolls. That was 652 of the 2,747 dialogs measured to
    // scroll. At a 1280 × 870px window with a reach of 365 days the form
    // ended on "Upload prices", the minimum-stay switch in the 132px under it.
    const continuesBelow = useContentBelowFold(body);

    /** Put the fields on what the server holds: after a read, and when a change is dropped. */
    const showForm = useCallback((form: SavedForm) => {
        setEnabled(form.enabled);
        setDaysAhead(form.daysAhead);
        setSelectedRoomKinds(new Set(form.roomKindIds));
        setSelectedPlanId(form.ratePlanId);
        setSendPrices(form.sendPrices);
        setSendMinLos(form.sendMinLos);
    }, []);

    const loadSchedule = useCallback(async () => {
        setLoading(true);
        try {
            const [schedule, freq]: [NamedAutoSyncSchedule, { syncFrequencyMinutes: SyncFrequencyMinutes }] =
                await Promise.all([
                    fetchAutoSyncSchedule(connectionId),
                    fetchConnectionSyncFrequency(connectionId),
                ]);
            const read: SavedForm = {
                enabled: schedule.enabled,
                daysAhead: String(schedule.daysAhead),
                roomKindIds: schedule.roomKindIds ?? [],
                ratePlanId: schedule.ratePlanId ?? "",
                // A schedule saved before the scope existed reads as prices-only.
                sendPrices: schedule.sendPrices !== false,
                sendMinLos: schedule.sendMinLos === true,
            };
            showForm(read);
            setSaved(read);
            setFrequencyMinutes(freq.syncFrequencyMinutes ?? DEFAULT_SYNC_FREQUENCY_MINUTES);
            setPricePlanMissing(schedule.hasPricePlan === false);
            setStatusEnabled(schedule.enabled);
        } catch {
            toast.error(t.autoSync.loadError);
        } finally {
            setLoading(false);
        }
    }, [connectionId, showForm, t.autoSync.loadError]);

    // Load on mount for status badge
    useEffect(() => {
        loadSchedule();
    }, [loadSchedule]);

    // Reload when dialog opens
    useEffect(() => {
        if (open) {
            loadSchedule();
        }
    }, [open, loadSchedule]);

    const toggleRoomKind = (id: string) => {
        setSelectedRoomKinds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const clearRoomKinds = () => {
        setSelectedRoomKinds(new Set());
    };

    // A schedule that names no plan gets its property's base plan filled in as
    // the lists load. Nobody picked it, so it is what the form stands on
    // rather than a change to it: read as one, the dialog would ask about
    // unsaved changes on a form nobody touched.
    const adoptDefaultPlan = useCallback((id: string) => {
        setSelectedPlanId(id);
        setSaved((held) => (held.ratePlanId === "" ? { ...held, ratePlanId: id } : held));
    }, []);

    const unsaved = useMemo<UnsavedChange | null>(() => {
        if (enabled !== saved.enabled) return enabled ? "turnedOn" : "turnedOff";
        // Off, and saved off: the rest of the form is not on screen, and
        // nothing it holds is in force either way.
        if (!enabled) return null;
        const same =
            daysAhead === saved.daysAhead
            && selectedPlanId === saved.ratePlanId
            && sendPrices === saved.sendPrices
            && sendMinLos === saved.sendMinLos
            && selectedRoomKinds.size === saved.roomKindIds.length
            && saved.roomKindIds.every((id) => selectedRoomKinds.has(id));
        return same ? null : "changed";
    }, [enabled, daysAhead, selectedPlanId, sendPrices, sendMinLos, selectedRoomKinds, saved]);

    /** Close, with the fields back on what is saved: by Cancel, or once the question is answered. */
    const closeWithoutSaving = () => {
        setAskingToClose(false);
        showForm(saved);
        setOpen(false);
    };

    // The X, Escape and a click beside the dialog say nothing of what should
    // become of the form, and a switch reads as taking effect the moment it
    // is turned: StayGroup switched auto-sync on for six properties, closed
    // the dialog and saved none (OPT-46, Oct 2026). So those three ask first.
    // Cancel says what it means and does not.
    const handleOpenChange = (next: boolean) => {
        if (next) {
            setOpen(true);
            return;
        }
        // A save in flight closes the dialog itself once it lands.
        if (saving) return;
        if (unsaved) {
            setAskingToClose(true);
            return;
        }
        setOpen(false);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const roomKindIds = roomKindOptions.length > 0 && selectedRoomKinds.size === roomKindOptions.length
                ? []
                : [...selectedRoomKinds];
            await updateAutoSyncSchedule(connectionId, {
                enabled,
                daysAhead: parseInt(daysAhead, 10),
                roomKindIds,
                ratePlanId: selectedPlanId || null,
                sendPrices,
                sendMinLos: sendMinLos && minLosSupported,
            });
            setSaved({
                enabled,
                daysAhead,
                roomKindIds: [...selectedRoomKinds],
                ratePlanId: selectedPlanId,
                sendPrices,
                sendMinLos,
            });
            setStatusEnabled(enabled);
            // The switch decides whether prices leave at all, so it changes
            // what the sidebar's sync indicator says. It polls a minute
            // apart, so without this a switch the operator has just flipped
            // keeps showing its old state and reads as one that did not
            // register. Only after the write succeeds: a status that moved on
            // a failed save would be the same lie in the other direction.
            notifySyncConfigChanged();
            toast.success(t.autoSync.saveSuccess);
            setAskingToClose(false);
            setOpen(false);
        } catch {
            toast.error(t.autoSync.saveError);
        } finally {
            setSaving(false);
        }
    };

    // Under the switch: what its position means, and what is in force until it
    // is saved. It used to stand at the foot of the form and say that no
    // drafts were available under every schedule that was switched on, which
    // beside a full price plan read as the reason nothing was being sent. A
    // property has no prices to send only where the server says so of this
    // dialog's own property; otherwise the line says what a save does, at the
    // property's own cadence. And a switch turned off is not saved either:
    // the schedule goes on sending until it is.
    const switchHint = !enabled
        ? saved.enabled ? t.autoSync.stillOnUntilSaved : t.autoSync.disabled
        : pricePlanMissing
            ? t.autoSync.noPricesYet
            : t.autoSync.sendsOnSave.replace("{cadence}", syncCadencePhrase(frequencyMinutes, t));

    // At once, like every scroll the product makes itself: nothing here
    // animates one. The browser stops it at the form's end.
    const scrollOn = () => {
        if (body) body.scrollTop += Math.round(body.clientHeight * PAGE_SHARE);
    };

    const unsavedSentence = unsaved === "turnedOn"
        ? t.autoSync.unsavedTurnedOn
        : unsaved === "turnedOff"
            ? t.autoSync.unsavedTurnedOff
            : t.autoSync.unsavedChanged;

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            {!isControlled && (
                <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                        <Timer className="h-4 w-4 mr-1.5" />
                        {t.autoSync.configure}
                        {statusEnabled !== null && (
                            <span
                                className={`ml-2 inline-block h-2 w-2 rounded-full ${statusEnabled ? "bg-success" : "bg-muted-foreground/40"
                                    }`}
                            />
                        )}
                    </Button>
                </DialogTrigger>
            )}
            {/* Taller than a laptop window leaves it in Czech (910px before the
                note past 90 days), so it is held to the window, and only its
                body scrolls: the title bar and the buttons stand outside the
                part that does. The whole dialog used to scroll, footer and
                all, and with the switch on an 870px window ended the box on
                its last note in every skin and language, Cancel and Save
                under its edge. macOS draws no scrollbar until something
                scrolls, so nothing said the dialog went on, and a switch
                turned on there was never saved (OPT-46, Oct 2026).

                The height is the dialog primitive's own, the window's less
                a rem at each edge; the column and `overflow-hidden` are this
                dialog's, and take the primitive's own scroll off in favour
                of the body's. */}
            <DialogContent className="@container/auto-sync flex flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
                {/* As tall as the close button that stands in its corner, with
                    the title on that button's own centre line and ending
                    where its box begins (0.25rem in, 2.5rem wide). A quarter
                    rem more and the Czech title took two lines on a 320px
                    phone under high contrast.

                    That room is the bar's own, the dialog having no padding.
                    The header primitive keeps room of its first row for a
                    dialog that has one (1.25rem, on both sides on a phone).
                    On top of the bar's it took the title to two lines on a
                    320px phone, in every skin and both languages, so
                    `*:first:px-0` takes it off. */}
                <DialogHeader className="shrink-0 border-b border-border py-3.5 pl-6 pr-11 *:first:px-0">
                    <DialogTitle className="flex items-center gap-2">
                        <Timer className="h-5 w-5 shrink-0 text-primary" />
                        {t.autoSync.title}
                    </DialogTitle>
                </DialogHeader>

                {/* The one part that scrolls. The description goes with the
                    form: it is read once, and held with the title it would
                    cost a short window two more lines of the room the form
                    has.

                    Tab past the last control comes round to the first without
                    scrolling to it, and a button is left where it is while
                    any sliver of it shows, so a control the keyboard lands on
                    is brought into view here, with the body's own padding
                    kept beside it: brought in by the least it takes, it lay
                    against the body's edge with its focus ring cut off on
                    that side (1,368 of 11,112 real Tab presses). */}
                <div
                    ref={setBody}
                    data-slot="dialog-body"
                    className="min-h-0 flex-1 scroll-py-4 overflow-y-auto overflow-x-hidden px-6 py-4"
                    onFocus={revealKeyboardFocus}
                >
                    {/* Everything the body scrolls, in one sheet, so that the
                        sheet's height is the form's. The window holds the body
                        to a height of its own, and a form that grows inside it
                        (the switch turned on, a list that loads, a notice)
                        changes the size of nothing else that could be watched
                        (`useContentBelowFold`). */}
                    <div className="space-y-4">
                        <DialogDescription>
                            {t.autoSync.description}
                        </DialogDescription>

                        {loading ? (
                            <div className="flex items-center justify-center py-6">
                                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {/* The cadence, stated rather than offered. It decides
                                    when the prices configured below actually leave, so
                                    the dialog would be lying by omission without it —
                                    but how often Optivo polls somebody else's PMS is
                                    an operational decision support makes per account,
                                    so the only control for it is in the admin console.
                                    Plain text rather than a disabled Select: a control
                                    nobody can move reads as one that is broken. */}
                                <div>
                                    <p className="text-sm font-medium">{t.autoSync.frequencyLabel}</p>
                                    <p className="text-sm mt-1">{syncCadenceLabel(frequencyMinutes, t)}</p>
                                    <p className="text-xs text-muted-foreground mt-1.5">
                                        {t.autoSync.frequencyHelp}
                                    </p>
                                </div>

                                {/* Enable / Disable toggle, and what its position
                                    means straight under it: read with the switch,
                                    ahead of everything a schedule sets, and in
                                    view as the dialog opens on every window but a
                                    phone held sideways. At the foot of the form
                                    it was under the fold of a laptop's. */}
                                <div>
                                    <div className="flex items-center justify-between">
                                        <label htmlFor="auto-sync-toggle" className="text-sm font-medium">
                                            {t.autoSync.enabled}
                                        </label>
                                        <Switch
                                            id="auto-sync-toggle"
                                            checked={enabled}
                                            onCheckedChange={setEnabled}
                                            aria-describedby="auto-sync-toggle-hint"
                                        />
                                    </div>
                                    <p
                                        id="auto-sync-toggle-hint"
                                        className={`mt-1.5 text-xs ${enabled && pricePlanMissing ? "text-foreground" : "text-muted-foreground"}`}
                                    >
                                        {switchHint}
                                    </p>
                                </div>

                                {enabled && (
                                    <>
                                        {/* Days ahead selector, up to the year and a half the
                                            price plan covers. Past the near horizon the dialog
                                            says the two things that change there: those
                                            nights are recalculated once a day, and one
                                            sync sends at most that many nights. */}
                                        <div>
                                            <label htmlFor="auto-sync-days-ahead" className="text-sm font-medium mb-1.5 block">
                                                {t.autoSync.daysAheadLabel}
                                            </label>
                                            <Select value={daysAhead} onValueChange={setDaysAhead}>
                                                <SelectTrigger id="auto-sync-days-ahead" className="w-full">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="1">{t.autoSync.daysAhead1}</SelectItem>
                                                    <SelectItem value="3">{t.autoSync.daysAhead3}</SelectItem>
                                                    <SelectItem value="7">{t.autoSync.daysAhead7}</SelectItem>
                                                    <SelectItem value="14">{t.autoSync.daysAhead14}</SelectItem>
                                                    <SelectItem value="30">{t.autoSync.daysAhead30}</SelectItem>
                                                    <SelectItem value="60">{t.autoSync.daysAhead60}</SelectItem>
                                                    <SelectItem value="90">{t.autoSync.daysAhead90}</SelectItem>
                                                    <SelectItem value="180">{t.autoSync.daysAhead180}</SelectItem>
                                                    <SelectItem value="365">{t.autoSync.daysAhead365}</SelectItem>
                                                    <SelectItem value={String(FULL_HORIZON_DAYS)}>{t.autoSync.daysAhead548}</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            {Number(daysAhead) > NEAR_HORIZON_DAYS && (
                                                <p className="text-xs text-muted-foreground mt-1.5">
                                                    {t.autoSync.farHorizonHint.replaceAll("{days}", String(NEAR_HORIZON_DAYS))}
                                                </p>
                                            )}
                                        </div>

                                        {/* Room kinds + rate plan (shared component),
                                            read for this dialog's own connection: what
                                            is picked here is saved onto its schedule,
                                            and the active property is only one of the
                                            cards that open this dialog. */}
                                        <PmsSendFilters
                                            connectionId={connectionId}
                                            roomKindOptions={roomKindOptions}
                                            selectedRoomKinds={selectedRoomKinds}
                                            onToggleRoomKind={toggleRoomKind}
                                            onClearRoomKinds={clearRoomKinds}
                                            selectedPlanId={selectedPlanId}
                                            onPlanChange={setSelectedPlanId}
                                            onPlanDefaulted={adoptDefaultPlan}
                                            loadOnMount={open && enabled}
                                            showSendScope
                                            sendPrices={sendPrices}
                                            onSendPricesChange={setSendPrices}
                                            sendMinLos={sendMinLos}
                                            onSendMinLosChange={setSendMinLos}
                                            minLosSupported={minLosSupported}
                                            minLosNotice={(
                                                <>
                                                    {minStayScope?.scope === "night" && (
                                                        <MinStayPerNightNotice
                                                            providerType={minStayScope.providerType}
                                                            hotelSetting={minStayScope.read}
                                                        />
                                                    )}
                                                    {sendsMinStayAlone && provider && (
                                                        <MinStayAloneNotice providerType={provider.providerType} />
                                                    )}
                                                    {/* True of the switch itself, so it
                                                        waits on no answer: under whatever
                                                        the notices above could say. */}
                                                    <p className="mt-2 text-xs text-muted-foreground">
                                                        {t.autoSync.minLosChangeInOptivo.replaceAll(
                                                            "{pms}",
                                                            pmsDisplayName(t, provider?.providerType ?? ""),
                                                        )}
                                                    </p>
                                                </>
                                            )}
                                        />
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter className="relative shrink-0 flex-row justify-end border-t border-border px-6 py-4">
                    {/* On the footer's rule, for as long as something of the
                        form lies under it, and gone at the end of the scroll
                        and where the form fits whole. A row of no height
                        across the footer's upper edge, out of its flow and
                        taking no click of its own, with the words centred
                        on it.

                        Not in the Tab order: Tab already brings each control
                        into view, and a stop that comes and goes with the
                        scroll would move the order under the keyboard.

                        And not while the schedule is read: there is no form
                        yet to continue, and on a phone held sideways under
                        high contrast it stood over the spinner, whose own
                        padding was all the fold hid. */}
                    {continuesBelow && !loading && (
                        <div className="pointer-events-none absolute inset-x-0 top-0 flex h-0 justify-center">
                            <button
                                type="button"
                                data-slot="dialog-continues"
                                tabIndex={-1}
                                className={CONTINUES_BELOW}
                                onMouseDown={keepFocus}
                                onClick={scrollOn}
                            >
                                <ChevronDown className="size-4" aria-hidden="true" />
                                {t.autoSync.continuesBelow}
                            </button>
                        </div>
                    )}
                    <Button variant="outline" className={FOOTER_BUTTON} onClick={closeWithoutSaving}>
                        {t.common.cancel}
                    </Button>
                    <Button
                        className={FOOTER_BUTTON}
                        onClick={handleSave}
                        disabled={saving || loading || (enabled && !sendPrices && !sendMinLos)}
                    >
                        {saving ? t.common.saving : t.common.save}
                    </Button>
                </DialogFooter>
            </DialogContent>

            {/* The question, over the dialog it is about. */}
            <AlertDialog open={askingToClose} onOpenChange={setAskingToClose}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t.autoSync.unsavedTitle}</AlertDialogTitle>
                        <AlertDialogDescription>{unsavedSentence}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t.autoSync.unsavedKeepEditing}</AlertDialogCancel>
                        <AlertDialogAction variant="destructive" onClick={closeWithoutSaving}>
                            {t.autoSync.unsavedDiscard}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Dialog>
    );
}
